import { PERMISSIONS as P, hasOrganizationPermission } from 'utils/organizationPermissions';

/**
 * Single source for chart labels, hub copy, and page titles.
 * pathKey must match PATH_APP.application.chart keys and descriptionKey in registry where set.
 */
export const CHART_HUB_SECTIONS = [
  {
    id: 'revenue',
    title: 'Revenue',
    description: 'Cash and recurring revenue over time',
    items: [
      {
        pathKey: 'revenue',
        label: 'Revenue',
        subtitle: 'Gross revenue by payment date (new vs renewal)',
        descriptionKey: 'revenue',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:currency-usd',
      },
      {
        pathKey: 'mrr',
        label: 'MRR',
        subtitle: 'Normalized monthly recurring revenue',
        descriptionKey: 'mrr',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-line',
      },
      {
        pathKey: 'arr',
        label: 'ARR',
        subtitle: 'Annualized run rate from MRR',
        descriptionKey: 'arr',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-areaspline',
      },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions',
    description: 'Subscriber counts, status, retention, and events',
    items: [
      {
        pathKey: 'activeSubscribers',
        label: 'Active subscribers',
        subtitle: 'Paying subscribers over time',
        descriptionKey: 'activeSubscribers',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:account-multiple',
      },
      {
        pathKey: 'activeSubscriptionsMovement',
        label: 'Subscription balance changes',
        subtitle: 'Net adds and losses by period',
        descriptionKey: 'activeSubscriptionsMovement',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-timeline-variant',
      },
      {
        pathKey: 'subscriptionRetention',
        label: 'Subscription retention',
        subtitle: 'Retention curves for subscriber cohorts',
        descriptionKey: 'subscriptionRetention',
        permissions: [P.NAV_CHARTS, P.FEATURE_SUBSCRIPTION_RETENTION],
        icon: 'mdi:chart-line',
      },
      {
        pathKey: 'subscriptionEvents',
        label: 'Subscription events',
        subtitle: 'Starts, renewals, and expirations',
        descriptionKey: 'subscriptionEvents',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:event',
      },
      {
        pathKey: 'autoRenewStatus',
        label: 'Auto-renew status',
        subtitle: 'On vs off before term end',
        descriptionKey: 'autoRenewStatus',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:refresh',
      },
      {
        pathKey: 'subscriptionStatus',
        label: 'Subscription status',
        subtitle: 'Distribution of subscription states',
        descriptionKey: 'subscriptionStatus',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-box',
      },
    ],
  },
  {
    id: 'cohorts-ltv',
    title: 'Cohorts & LTV',
    description: 'LTV & payback explorers, conversion matrices, and lifetime value',
    items: [
      {
        pathKey: 'cohortExplorer',
        label: 'Cohort LTV Explorer',
        subtitle: 'Revenue, retention & realized LTV by signup cohort',
        descriptionKey: 'cohortExplorer',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-timeline-variant',
      },
      {
        pathKey: 'cohortExplorerRecoup',
        label: 'Cohort Payback Explorer',
        subtitle: 'Net ROAS, spend recovery & payer CAC by cohort',
        descriptionKey: 'cohortExplorerRecoup',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-bell-curve',
      },
      {
        pathKey: 'cohortedInitialConversion',
        label: 'Initial conversion by cohort',
        subtitle: 'Signup-cohort matrix · first purchase rate',
        descriptionKey: 'cohortedInitialConversion',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:funnel-outline',
        cohortView: true,
      },
      {
        pathKey: 'cohortedTrialConversion',
        label: 'Trial conversion by cohort',
        subtitle: 'Signup-cohort matrix · trial-to-paid',
        descriptionKey: 'cohortedTrialConversion',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:clock-check-outline',
        cohortView: true,
      },
      {
        pathKey: 'cohortedIntroOfferConversion',
        label: 'Intro offer conversion by cohort',
        subtitle: 'Signup-cohort matrix · intro offer uptake',
        descriptionKey: 'cohortedIntroOfferConversion',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:tag-outline',
        cohortView: true,
      },
      {
        pathKey: 'cohortedConversionToPaying',
        label: 'Conversion to paying by cohort',
        subtitle: 'Signup-cohort matrix · paying subscriber rate',
        descriptionKey: 'cohortedConversionToPaying',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:currency-usd',
        cohortView: true,
      },
      {
        pathKey: 'cohortedReactivation',
        label: 'Reactivation by cohort',
        subtitle: 'Win-back rate · initial-conversion / new-paying cohorts',
        descriptionKey: 'cohortedReactivation',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:account-reactivate-outline',
        cohortView: true,
      },
      {
        pathKey: 'ltvPrediction',
        label: 'LTV Prediction',
        subtitle: "Today's subscribers' next 12 months · renewals at risk · accuracy",
        permissions: [P.NAV_CHARTS, P.FEATURE_LTV],
        icon: 'mdi:crystal-ball',
      },
      {
        pathKey: 'LTVPerPayingUsers',
        label: 'LTV (paying users)',
        subtitle: 'Per paying customer, by first payment · in Cohort LTV Explorer',
        descriptionKey: 'LTVPerPayingUsers',
        permissions: [P.NAV_CHARTS, P.FEATURE_LTV],
        icon: 'mdi:cash-multiple',
      },
      {
        pathKey: 'LTVPerAllUsers',
        label: 'LTV (all users)',
        subtitle: 'Per new user, payers or not · in Cohort LTV Explorer',
        descriptionKey: 'LTVPerAllUsers',
        permissions: [P.NAV_CHARTS, P.FEATURE_LTV],
        icon: 'mdi:account-cash',
      },
    ],
  },
  {
    id: 'conversion',
    title: 'Conversion funnel',
    description: 'Conversion rates over calendar time (not cohort matrices)',
    items: [
      {
        pathKey: 'conversionToPaying',
        label: 'Conversion to paying',
        subtitle: 'Share of users who become paying subscribers',
        descriptionKey: 'conversionToPaying',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:arrow-right-circle',
      },
      {
        pathKey: 'initialConversion',
        label: 'Initial conversion',
        subtitle: 'First purchase rate over time',
        descriptionKey: 'initialConversion',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:flag-outline',
      },
      {
        pathKey: 'funnel',
        label: 'Funnel',
        subtitle: 'Trial → paid → renewals, by any breakdown',
        descriptionKey: 'funnel',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:filter-variant',
      },
      {
        pathKey: 'trialConversion',
        label: 'Trial conversion',
        subtitle: 'Trial-to-paid rate over time',
        descriptionKey: 'trialConversion',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:funnel-outline',
      },
      {
        pathKey: 'offerConversion',
        label: 'Offer conversion',
        subtitle: 'Intro offer-to-paid rate over time',
        descriptionKey: 'offerConversion',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:tag-outline',
      },
      {
        pathKey: 'reactivation',
        label: 'Reactivation',
        subtitle: 'Win-backs over time',
        descriptionKey: 'reactivation',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:account-reactivate-outline',
      },
    ],
  },
  {
    id: 'trials',
    title: 'Trials',
    description: 'Trial counts and balance changes',
    items: [
      {
        pathKey: 'activeTrials',
        label: 'Active trials',
        subtitle: 'Users in trial over time',
        descriptionKey: 'activeTrials',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:clock-outline',
      },
      {
        pathKey: 'newTrials',
        label: 'New trials',
        subtitle: 'Trial starts per period',
        descriptionKey: 'newTrials',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:plus-circle-outline',
      },
      {
        pathKey: 'activeTrialsMovement',
        label: 'Trial balance changes',
        subtitle: 'Net trial adds and losses by period',
        descriptionKey: 'activeTrialsMovement',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-timeline-variant',
      },
    ],
  },
  {
    id: 'churn',
    title: 'Churn & refunds',
    description: 'Churn, refunds, and cancellation insights',
    items: [
      {
        pathKey: 'churn',
        label: 'Churn',
        subtitle: 'Subscriber loss rate by period',
        descriptionKey: 'churn',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:chart-timeline-variant',
      },
      {
        pathKey: 'refundRate',
        label: 'Refund rate',
        subtitle: 'Refunded revenue share',
        descriptionKey: 'refundRate',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:percent',
      },
      {
        pathKey: 'appStoreRefundRequests',
        label: 'App Store refund requests',
        subtitle: 'Apple refund outcomes and reasons',
        descriptionKey: 'appStoreRefundRequests',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:apple',
      },
      {
        pathKey: 'playStoreCancelReasons',
        label: 'Play Store cancel reasons',
        subtitle: 'Google Play cancellation motives',
        descriptionKey: 'playStoreCancelReasons',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:google-play',
      },
      {
        pathKey: 'cancellationTiming',
        label: 'Cancellation timing',
        subtitle: 'When users cancel relative to renewal',
        descriptionKey: 'cancellationTiming',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:clock-outline',
      },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing & acquisition',
    description: 'AppsFlyer campaign ROI vs geo-level Cost ETL spend',
    items: [
      {
        pathKey: 'trafficQuality',
        label: 'Traffic quality',
        subtitle: 'Is a source delivering users it actually created?',
        descriptionKey: 'trafficQuality',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:shield-alert-outline',
      },
      {
        pathKey: 'adROI',
        label: 'Ad ROI (AppsFlyer)',
        subtitle: 'Campaign & store breakdown · sync from AppsFlyer',
        descriptionKey: 'adROI',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:bullhorn-outline',
        sections: {
          summary: 'Period summary',
          trend: 'Trends over time',
          comparison: 'Unit costs & ROAS',
          table: 'Campaign breakdown',
        },
      },
      {
        pathKey: 'costETL',
        label: 'Geo ad spend',
        subtitle: 'Country-level Cost ETL · CPI, funnel & ROAS',
        descriptionKey: 'costETL',
        permissions: [P.NAV_CHARTS],
        icon: 'mdi:database-outline',
        sections: {
          summary: 'Period summary',
          trend: 'Trends over time',
          comparison: 'Unit costs & ROAS',
          table: 'Cost breakdown',
        },
      },
    ],
  },
];

const CATALOG_BY_PATH_KEY = new Map();

CHART_HUB_SECTIONS.forEach((section) => {
  section.items.forEach((item) => {
    CATALOG_BY_PATH_KEY.set(item.pathKey, { ...item, sectionTitle: section.title });
  });
});

/** @returns {import('./chartHubCatalog').ChartCatalogItem | undefined} */
export function getChartCatalogItem(pathKey) {
  return CATALOG_BY_PATH_KEY.get(pathKey);
}

export function getChartHeading(pathKey, fallback) {
  return getChartCatalogItem(pathKey)?.label ?? fallback ?? pathKey;
}

export function getChartPageTitle(pathKey, fallbackHeading) {
  const label = getChartHeading(pathKey, fallbackHeading);
  const item = getChartCatalogItem(pathKey);
  if (item?.pageTitle) {
    return item.pageTitle;
  }
  return `${label} | Mobeat`;
}

export function getChartSectionTitle(pathKey, sectionKey, fallback) {
  const item = getChartCatalogItem(pathKey);
  return item?.sections?.[sectionKey] ?? fallback;
}

export function getChartHubSections(organizationRole) {
  return CHART_HUB_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => hasOrganizationPermission(organizationRole, item.permissions))
      .map(({ permissions, ...item }) => item),
  })).filter((section) => section.items.length > 0);
}
